import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  MessageSquare, Star, MapPin, Calendar, Camera, 
  ChevronLeft, ChevronRight, X, Instagram, Mail, Phone,
  Award, Briefcase, Clock, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { demoPortfolio, getTemplateColor, getPalette, colorPalettes } from '../data/portfolioMock';

const MeuTrampoPublico = () => {
  const { slug } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trabalhos');
  const [selectedWork, setSelectedWork] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);

  // Get colors based on palette
  const getColors = () => {
    if (!portfolio) return colorPalettes[0];
    
    if (portfolio.useCustomColors && portfolio.customColors) {
      return {
        primary: portfolio.customColors.primary,
        secondary: portfolio.customColors.secondary,
        accent: portfolio.customColors.accent,
        gradient: `from-[${portfolio.customColors.primary}] to-[${portfolio.customColors.secondary}]`
      };
    }
    
    return getPalette(portfolio.colorPalette) || colorPalettes[0];
  };

  const colors = portfolio ? getColors() : colorPalettes[0];

  useEffect(() => {
    const loadPortfolio = async () => {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      try {
        const response = await fetch(`${API_URL}/api/portfolio/professionals/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPortfolio(data);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.log('Fallback to localStorage/demo');
      }
      
      const stored = localStorage.getItem(`portfolio_${slug}`);
      if (stored) {
        setPortfolio(JSON.parse(stored));
      } else if (slug === 'joao-reformas' || slug === 'demo') {
        setPortfolio(demoPortfolio);
      }
      setLoading(false);
    };

    loadPortfolio();
  }, [slug]);

  const handleWhatsApp = () => {
    const message = `Olá! Vi seu portfólio e gostaria de mais informações sobre seus serviços.`;
    const phone = portfolio?.phone?.replace(/\D/g, '');
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getColorClasses = () => {
    if (portfolio?.useCustomColors && portfolio?.customColors) {
      return ''; // Will use inline style
    }
    const palette = getPalette(portfolio?.colorPalette);
    return palette?.gradient || 'from-purple-500 to-indigo-500';
  };

  const getHeaderStyle = () => {
    if (portfolio?.useCustomColors && portfolio?.customColors) {
      return {
        background: `linear-gradient(to right, ${portfolio.customColors.primary}, ${portfolio.customColors.secondary})`
      };
    }
    return {};
  };

  const averageRating = portfolio?.testimonials?.length > 0
    ? (portfolio.testimonials.reduce((acc, t) => acc + t.rating, 0) / portfolio.testimonials.length).toFixed(1)
    : '5.0';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
        <Briefcase size={48} className="text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Portfólio não encontrado</h1>
        <p className="text-slate-600 mb-6">Este profissional ainda não criou seu portfólio.</p>
        <Button onClick={() => window.location.href = '/meutrampo'} className="bg-purple-600 hover:bg-purple-700">
          Criar meu portfólio
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header/Cover */}
      <div 
        className={`h-48 sm:h-56 relative ${!portfolio.useCustomColors ? `bg-gradient-to-r ${getColorClasses()}` : ''}`}
        style={getHeaderStyle()}
      >
        {portfolio.coverPhoto && (
          <img 
            src={portfolio.coverPhoto} 
            alt="Capa"
            className="w-full h-full object-cover absolute inset-0"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-black/20" />
      </div>

      {/* Profile Card */}
      <div className="max-w-2xl mx-auto px-4 -mt-20 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {/* Avatar e Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-6">
            <img 
              src={portfolio.photo || 'https://via.placeholder.com/100'}
              alt={portfolio.name}
              className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
            />
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl font-bold text-slate-900">{portfolio.business || portfolio.name}</h1>
              {portfolio.tagline && (
                <p className="text-slate-600 text-sm mb-2">{portfolio.tagline}</p>
              )}
              <div className="flex flex-wrap justify-center sm:justify-start gap-3 text-sm text-slate-500">
                {portfolio.city && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {portfolio.city}
                  </span>
                )}
                {portfolio.yearsExperience && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {portfolio.yearsExperience} anos
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{portfolio.works?.length || 0}</p>
              <p className="text-xs text-slate-500">Trabalhos</p>
            </div>
            <div className="text-center border-x border-slate-200">
              <p className="text-2xl font-bold text-slate-900">{portfolio.testimonials?.length || 0}</p>
              <p className="text-xs text-slate-500">Avaliações</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Star size={18} className="fill-yellow-400 text-yellow-400" />
                <span className="text-2xl font-bold text-slate-900">{averageRating}</span>
              </div>
              <p className="text-xs text-slate-500">Nota</p>
            </div>
          </div>

          {/* Categories */}
          {portfolio.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6 justify-center sm:justify-start">
              {portfolio.categories.map((cat, i) => (
                <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                  {cat}
                </span>
              ))}
            </div>
          )}

          {/* Bio */}
          {portfolio.bio && (
            <p className="text-slate-600 text-sm mb-6 text-center sm:text-left">
              {portfolio.bio}
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={handleWhatsApp}
              className="flex-1 bg-green-500 hover:bg-green-600"
            >
              <MessageSquare size={18} />
              WhatsApp
            </Button>
            {portfolio.instagram && (
              <Button 
                variant="outline"
                onClick={() => window.open(`https://instagram.com/${portfolio.instagram.replace('@', '')}`, '_blank')}
              >
                <Instagram size={18} />
              </Button>
            )}
            {portfolio.email && (
              <Button 
                variant="outline"
                onClick={() => window.location.href = `mailto:${portfolio.email}`}
              >
                <Mail size={18} />
              </Button>
            )}
          </div>

          {/* Share buttons */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2 text-center">Compartilhar este portfólio:</p>
            <div className="flex gap-2 justify-center">
              <button 
                onClick={() => {
                  const url = window.location.href;
                  const msg = `Veja o portfólio de ${portfolio.business || portfolio.name}: ${url}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                }}
                className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1 hover:bg-green-200"
              >
                <MessageSquare size={12} />
                WhatsApp
              </button>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copiado!');
                }}
                className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-xs flex items-center gap-1 hover:bg-slate-200"
              >
                <ExternalLink size={12} />
                Copiar link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-2xl mx-auto px-4 mt-6">
        <div className="flex gap-2 bg-white rounded-xl p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('trabalhos')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'trabalhos'
                ? 'bg-purple-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Camera size={18} className="inline mr-2" />
            Trabalhos
          </button>
          <button
            onClick={() => setActiveTab('avaliacoes')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'avaliacoes'
                ? 'bg-purple-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Star size={18} className="inline mr-2" />
            Avaliações
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {activeTab === 'trabalhos' && (
          <div className="space-y-4">
            {portfolio.works?.length > 0 ? (
              portfolio.works.map((work) => (
                <div 
                  key={work.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all"
                  onClick={() => { setSelectedWork(work); setImageIndex(0); }}
                >
                  <div className="aspect-video relative">
                    <img 
                      src={work.images[0]?.url}
                      alt={work.title}
                      className="w-full h-full object-cover"
                    />
                    {work.images.length > 1 && (
                      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
                        +{work.images.length - 1} fotos
                      </div>
                    )}
                    <div className="absolute top-2 left-2 px-2 py-1 bg-white/90 text-slate-700 text-xs rounded-full">
                      {work.category}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-1">{work.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{work.description}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Camera size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nenhum trabalho adicionado ainda</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'avaliacoes' && (
          <div className="space-y-4">
            {portfolio.testimonials?.length > 0 ? (
              portfolio.testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <img 
                      src={testimonial.photo || 'https://via.placeholder.com/50'}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-slate-900">{testimonial.name}</h4>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <Star 
                              key={i} 
                              size={14} 
                              className={i <= testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm mb-2">{testimonial.text}</p>
                      <p className="text-xs text-slate-400">
                        {testimonial.service} • {testimonial.date}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Star size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nenhuma avaliação ainda</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedWork && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button 
            onClick={() => setSelectedWork(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <X size={24} />
          </button>
          
          <button 
            onClick={() => setImageIndex(prev => prev > 0 ? prev - 1 : selectedWork.images.length - 1)}
            className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronLeft size={32} />
          </button>
          
          <div className="max-w-3xl w-full">
            <img 
              src={selectedWork.images[imageIndex]?.url}
              alt={selectedWork.title}
              className="w-full rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white font-semibold text-lg">{selectedWork.title}</h3>
              <p className="text-white/70 text-sm">{selectedWork.images[imageIndex]?.caption}</p>
              <p className="text-white/50 text-xs mt-2">
                {imageIndex + 1} de {selectedWork.images.length}
              </p>
            </div>
          </div>
          
          <button 
            onClick={() => setImageIndex(prev => prev < selectedWork.images.length - 1 ? prev + 1 : 0)}
            className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 text-center border-t border-slate-200 bg-white mt-8">
        <p className="text-xs text-slate-400">
          Portfólio criado com <a href="/meutrampo" className="text-purple-600 hover:underline">Meu Trampo</a> by AtivaJá
        </p>
      </footer>
    </div>
  );
};

export default MeuTrampoPublico;
